import React, { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from "~/hooks/redux";
import Preloader from "~/components/Shared/Preloader";
import { getAuthUser } from "~/store/actions";
import { initTimezone } from '~/helpers/dateHelper';
import { NetworkErrorException } from '~/helpers/errorHelper';
import ErrorComponent from '~/pages/Error';
import User from '~/model/user';

type AuthContextValue = {
  user: User | null;
  isLoggedIn: () => boolean;
  isImpersonation: () => boolean;
  refreshAuthUser: () => void;
};

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

type AuthProviderProps = {
  children: React.ReactNode;
};

const AuthProvider = (props: AuthProviderProps) => {
  const dispatch = useAppDispatch();

  // warning
  // useSelector() will cause a re-render whenever the result of its callback function changes
  // for example: const { user, isAuth } = useSelector(state => state.Auth.Login);
  // you might expect to get a re-render only when 'user' or 'isAuth' change
  // in fact you will get a re-render when anything inside 'state.Auth.Login' changes, not just 'user' and 'isAuth'
  // so if that object contains other properties, unwanted re-renders will happen when they change
  // that is why, instead of destructuring, we target specific state properties one by one
  // https://9oelm.github.io/2020-09-13--How-to-make-useSelector-not-a-disaster/#function-component
  const user = useAppSelector(state => state.Auth.Login.user);
  const isAuth = useAppSelector(state => state.Auth.Login.isAuth);
  const userError = useAppSelector(state => state.Auth.Login.userError);

  const refreshAuthUser = useCallback(() => {
    dispatch(getAuthUser());
  }, [dispatch]);

  const isLoggedIn = useCallback((): boolean => !!user, [user]);

  const isImpersonation = useCallback((): boolean => !!(user as any)?.isImpersonated, [user]);

  const value = useMemo(
    () => ({ user, isLoggedIn, isImpersonation, refreshAuthUser }),
    [user, isLoggedIn, isImpersonation, refreshAuthUser]
  );

  useEffect(() => {
    if (isAuth === null) {
      refreshAuthUser();
    }
  }, [isAuth, refreshAuthUser]);

  if (!!user) {
    initTimezone((user as any).timezone);
  }

  // note: when isAuth === null (undecided, /me not yet resolved), we deliberately render children
  // rather than blocking them with a <Preloader />. this allows ProfileContext — which is a child in
  // the provider stack — to mount immediately and dispatch its own /me/notary-profile request in
  // parallel with our /me request, instead of waiting for auth to resolve first (sequential waterfall).
  // ProfileContext owns the single loading gate: it waits until both isAuth and notary are resolved
  // before rendering the app, so the user still sees exactly one preloader on startup.
  if (userError instanceof NetworkErrorException) {
    return <ErrorComponent error={userError} />;
  }
  return <AuthContext.Provider value={value} {...props} />;
};

// helper hook that makes context data available
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;

